import { Component } from '@theme/component';

/**
 * About / Video overlay player (custom product page only).
 *
 * Wraps a native Shopify <video> (rendered by the `video_tag` filter) and wires
 * an always-visible, accessible play/pause <button> to it. State is driven by the
 * video's REAL `play`/`pause`/`ended` events (not an assumption that autoplay
 * succeeded), and the button's aria-label is kept in sync. No third-party code,
 * no global/document listeners, no duplicate registration.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} control - The overlay play/pause button.
 * @extends {Component<Refs>}
 */
class AboutVideoPlayerN extends Component {
  requiredRefs = ['control'];

  #controller = new AbortController();

  /** @returns {HTMLVideoElement | null} */
  get video() {
    return this.querySelector('video');
  }

  connectedCallback() {
    super.connectedCallback();

    const { video } = this;
    if (!video) return;

    const { signal } = this.#controller;
    video.addEventListener('play', this.#handlePlay, { signal });
    video.addEventListener('playing', this.#handlePlay, { signal });
    video.addEventListener('pause', this.#handlePause, { signal });
    video.addEventListener('ended', this.#handleEnded, { signal });

    // Respect reduced motion: do not autoplay — begin paused (Play state).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && video.autoplay) {
      video.autoplay = false;
      video.pause();
    }

    // Initialise the control from the video's actual state.
    this.#sync(!video.paused && !video.ended);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** Declarative `on:click="/toggle"` handler. */
  toggle() {
    const { video } = this;
    if (!video) return;

    if (video.paused || video.ended) {
      const played = video.play();
      // Autoplay/gesture policies may reject play() — reflect the real result.
      if (played && typeof played.catch === 'function') {
        played.catch(() => this.#sync(false));
      }
    } else {
      video.pause();
    }
  }

  #handlePlay = () => this.#sync(true);
  #handlePause = () => this.#sync(false);
  #handleEnded = () => this.#sync(false);

  /**
   * Reflect the playing state on the host (for CSS icon swap) and the button label.
   * @param {boolean} isPlaying
   */
  #sync(isPlaying) {
    this.dataset.playing = isPlaying ? 'true' : 'false';

    const { control } = this.refs;
    if (control instanceof HTMLElement) {
      const label = isPlaying ? control.dataset.labelPause : control.dataset.labelPlay;
      if (label) control.setAttribute('aria-label', label);
    }
  }
}

if (!customElements.get('about-video-player-n')) {
  customElements.define('about-video-player-n', AboutVideoPlayerN);
}
